import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, HostListener } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { GeocodingService } from '../../../../_services/geocoding.service';

@Component({
  selector: 'app-resource-day-map-modal',
  templateUrl: './resource-day-map-modal.component.html',
  styleUrls: ['./resource-day-map-modal.component.css']
})
export class ResourceDayMapModalComponent implements OnChanges {
  @Input() team: any;
  @Input() day: any;
  @Input() events: any[] = [];

  @Output() closeModal = new EventEmitter<void>();

  geocodedMarkers: { event: any; lat: number; lng: number }[] = [];
  geocodingInProgress = false;
  geocodingMessage = '';
  showGeocodePrompt = false;
  userDeclinedGeocode = false;
  eventsNeedingGeocodeCount = 0;

  constructor(private geocoding: GeocodingService) {}

  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['events']) {
        this.geocodedMarkers = [];
        this.userDeclinedGeocode = false;
        this.showGeocodePrompt = false;
        this.updateGeocodePrompt();
      }
    } catch (e) {
      console.warn('ResourceDayMapModal ngOnChanges', e);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  private updateGeocodePrompt(): void {
    try {
      const list = this.getEventsNeedingGeocode();
      this.eventsNeedingGeocodeCount = list.length;
      this.showGeocodePrompt = list.length > 0 && !this.geocodingInProgress && !this.userDeclinedGeocode;
    } catch (_) {
      this.showGeocodePrompt = false;
    }
  }

  getEventsNeedingGeocode(): { event: any; address: string }[] {
    const result: { event: any; address: string }[] = [];
    try {
      const list = this.events || [];
      const max = Math.min(list.length, 300);
      for (let i = 0; i < max; i++) {
        const event = list[i];
        if (this.getEventLat(event) != null && this.getEventLng(event) != null) continue;
        const address = this.getEventAddressString(event);
        if (address?.trim()) result.push({ event, address: address.trim() });
      }
    } catch (_) {}
    return result;
  }

  confirmLoadCoords(): void {
    this.showGeocodePrompt = false;
    this.loadMissingCoords();
  }

  declineLoadCoords(): void {
    this.showGeocodePrompt = false;
    this.userDeclinedGeocode = true;
  }

  private loadMissingCoords(): void {
    if (this.geocodingInProgress) return;
    const needGeocode = this.getEventsNeedingGeocode();
    if (needGeocode.length === 0) {
      this.geocodingInProgress = false;
      this.geocodingMessage = '';
      return;
    }
    this.geocodingInProgress = true;
    this.geocodingMessage = needGeocode.length === 1
      ? 'Koordinaten werden ermittelt …'
      : `Koordinaten werden für ${needGeocode.length} Adressen ermittelt …`;

    const requests = needGeocode.map(({ event, address }) =>
      this.geocoding.geocodeAddress(address).pipe(
        take(1),
        catchError(() => of({ lat: 0, lng: 0 }))
      )
    );

    forkJoin(requests).pipe(take(1)).subscribe({
      next: (results: { lat: number; lng: number }[]) => {
        try {
          const markers: { event: any; lat: number; lng: number }[] = [];
          (results || []).forEach((loc, i) => {
            if (i < needGeocode.length && loc && (loc.lat !== 0 || loc.lng !== 0)) {
              markers.push({ event: needGeocode[i].event, lat: loc.lat, lng: loc.lng });
            }
          });
          this.geocodedMarkers = markers;
          this.updateGeocodePrompt();
        } finally {
          this.geocodingInProgress = false;
          this.geocodingMessage = '';
        }
      },
      error: () => {
        this.geocodingInProgress = false;
        this.geocodingMessage = '';
        this.updateGeocodePrompt();
      }
    });
  }

  private getEventAddressString(event: any): string {
    const d = event?.data || event?.order || {};
    const root = d?.root_order || d?.rootOrder || {};
    const person = root?.person || {};
    const street = d?.street ?? root?.street ?? person?.street ?? '';
    const postalCode = d?.postal_code ?? root?.postal_code ?? person?.postal_code ?? '';
    const place = d?.place ?? root?.place ?? person?.place ?? '';
    const parts = [street, [postalCode, place].filter(Boolean).join(' '), 'Deutschland'].filter(Boolean);
    return parts.join(', ');
  }

  get mapLat(): number {
    try {
      const first = this.allEventsWithCoords[0];
      return first?.lat ?? 51.1657;
    } catch (_) { return 51.1657; }
  }

  get mapLng(): number {
    try {
      const first = this.allEventsWithCoords[0];
      return first?.lng ?? 10.4515;
    } catch (_) { return 10.4515; }
  }

  get mapZoom(): number {
    try {
      return this.allEventsWithCoords.length <= 1 ? 10 : 8;
    } catch (_) { return 10; }
  }

  get allEventsWithCoords(): { event: any; lat: number; lng: number }[] {
    try {
      const withStatic: { event: any; lat: number; lng: number }[] = [];
      const list = this.events || [];
      const max = Math.min(list.length, 300);
      for (let i = 0; i < max; i++) {
        try {
          const event = list[i];
          const lat = this.getEventLat(event);
          const lng = this.getEventLng(event);
          if (lat != null && lng != null) withStatic.push({ event, lat, lng });
        } catch (_) {}
      }
      return [...withStatic, ...(this.geocodedMarkers || [])];
    } catch (_) {
      return [...(this.geocodedMarkers || [])];
    }
  }

  private getEventLat(event: any): number | null {
    try {
      const d = event?.data || event?.order || {};
      const root = d?.root_order || d?.rootOrder || {};
      const person = root?.person || {};
      const v = d?.lat ?? person?.lat ?? root?.lat ?? null;
      return v != null ? Number(v) : null;
    } catch (_) { return null; }
  }

  private getEventLng(event: any): number | null {
    try {
      const d = event?.data || event?.order || {};
      const root = d?.root_order || d?.rootOrder || {};
      const person = root?.person || {};
      const v = d?.lng ?? person?.lng ?? root?.lng ?? null;
      return v != null ? Number(v) : null;
    } catch (_) { return null; }
  }

  getEventTimeDisplay(event: any): string {
    try {
      if (event?.time) return event.time;
      if (event?.timeStart && event?.timeEnd) return `${event.timeStart} - ${event.timeEnd}`;
      if (event?.start) return event.end ? `${event.start} - ${event.end}` : String(event.start);
    } catch (_) {}
    return '';
  }

  close(): void {
    try {
      this.closeModal.emit();
    } catch (e) {
      console.warn('ResourceDayMapModal close', e);
    }
  }
}
